import { Platform } from 'react-native';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import * as FileSystem from 'expo-file-system/legacy';

export type OptimizedStoreImage = {
  base64: string;
  mimeType: string;
};

/** Paridad con scripts/lib/normalizeStoreLogoBuffer (WhatsApp / og:image). */
const LOGO_OG_MIN = 200;
const LOGO_OG_MAX = 512;

async function readFileAsBase64(uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error('No se pudo leer la imagen optimizada.');
    }
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const commaIdx = result.indexOf(',');
          resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        } else {
          reject(new Error('No se pudo procesar la imagen optimizada.'));
        }
      };
      reader.onerror = () => reject(new Error('Error al leer la imagen optimizada.'));
      reader.readAsDataURL(blob);
    });
  }
  return FileSystem.readAsStringAsync(uri, { encoding: 'base64' as const });
}

async function finalizeLogo(uri: string): Promise<OptimizedStoreImage> {
  const result = await manipulateAsync(uri, [], {
    format: SaveFormat.JPEG,
    compress: 0.82,
    base64: true,
  });
  const base64 = result.base64 ?? (await readFileAsBase64(result.uri));
  return { base64, mimeType: 'image/jpeg' };
}

/** Comprime logo/portada antes de subir (evita 400 por file_size_limit del bucket). */
export async function optimizeStoreImage(
  uri: string,
  kind: 'logo' | 'cover',
): Promise<OptimizedStoreImage> {
  const quality = kind === 'logo' ? 0.82 : 0.78;
  if (kind === 'cover') {
    try {
      const result = await manipulateAsync(uri, [{ resize: { width: 1600 } }], {
        format: SaveFormat.JPEG,
        compress: quality,
        base64: true,
      });
      const base64 = result.base64 ?? (await readFileAsBase64(result.uri));
      return { base64, mimeType: 'image/jpeg' };
    } catch (err) {
      throw new Error(
        `No se pudo optimizar la imagen: ${err instanceof Error ? err.message : 'error desconocido'}`,
      );
    }
  }

  try {
    // Escala a ancho 512 (sube logos chicos, baja los grandes).
    let result = await manipulateAsync(uri, [{ resize: { width: LOGO_OG_MAX } }], {
      format: SaveFormat.JPEG,
      compress: quality,
      base64: false,
    });
    let w = result.width ?? LOGO_OG_MAX;
    let h = result.height ?? LOGO_OG_MAX;

    if (h < LOGO_OG_MIN) {
      result = await manipulateAsync(result.uri, [{ resize: { height: LOGO_OG_MIN } }], {
        format: SaveFormat.JPEG,
        compress: quality,
        base64: false,
      });
      w = result.width ?? w;
      h = result.height ?? LOGO_OG_MIN;
    }
    if (w > LOGO_OG_MAX) {
      result = await manipulateAsync(result.uri, [{ resize: { width: LOGO_OG_MAX } }], {
        format: SaveFormat.JPEG,
        compress: quality,
        base64: false,
      });
    }

    return finalizeLogo(result.uri);
  } catch (err) {
    throw new Error(
      `No se pudo optimizar la imagen: ${err instanceof Error ? err.message : 'error desconocido'}`,
    );
  }
}
